import { Routes, Route, Navigate } from 'react-router-dom';
import { BeheardShellLayout } from './shell/BeheardShellLayout';
import { AuthGuard, OnboardingGuard, AdminGuard, CapabilityGuard } from './components/routing';
import { BootGate } from './components/routing/BootGate';
import { NotificationProvider } from './context/NotificationProvider';
// Suite-standard settings hub built on @levelup/ui-management/settings.
import { SettingsPageV2 } from './pages/SettingsPageV2';
import { OffloadResponsibilitiesPage } from './pages/OffloadResponsibilitiesPage';
import { PracticePage } from './pages/PracticePage';
import { PracticeReviewPage } from './pages/PracticeReviewPage';
import { PetalsManagerPage } from './pages/PetalsManagerPage';
import { CoachingPage } from './pages/CoachingPage';
import { CoachingSessionResultPage } from './pages/CoachingSessionResultPage';
import { InterviewPrepPage } from './pages/InterviewPrepPage';
import { LanguageLearningPage, LanguageProgressPage } from './pages/LanguageLearningPage';
import { SongLearningPage } from './pages/SongLearningPage';
import { LearnPage } from './pages/LearnPage';
import { LearnModulePage } from './pages/learn/LearnModulePage';
import { MarketplacePage } from './pages/MarketplacePage';
import { ExpertsPage } from './pages/marketplace/ExpertsPage';
import { PitchesPage } from './pages/marketplace/PitchesPage';
import { ProgressPage } from './pages/ProgressPage';
import { ProfilePage } from './pages/ProfilePage';
import { PortfolioPage } from './pages/PortfolioPage';
import { PublicPortfolioPage } from './pages/PublicPortfolioPage';
import { LoginPage } from './pages/LoginPage';
import { PricingPage } from './pages/PricingPage';
import { BillingPage } from './pages/BillingPage';
import { SkillsPage } from './pages/SkillsPage';
import { HireRequestsPage } from './pages/HireRequestsPage';
import { OnboardingWizardPage } from './pages/OnboardingWizardPage';
import { HomeDashboardPage } from './pages/HomeDashboardPage';
import { AssessmentRunPage } from './pages/AssessmentRunPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { TryPage } from './pages/TryPage';
import { BlueprintEditorPage } from './pages/BlueprintEditorPage';
import { NpcProgressPage } from './pages/NpcProgressPage';
import { AdminDashboardPage } from './pages/admin';
import { AdminLandingPage } from './pages/admin/AdminLandingPage';
import { AdminUsageOverviewPage } from './pages/admin/AdminUsageOverviewPage';
import {
  ScenarioAssessmentPreviewPage,
  ScenarioAuthoringListPage,
  ScenarioAuthoringPage,
} from './pages/admin/ScenarioAuthoringPage';
import { CertificationsPage } from './pages/CertificationsPage';
import { UsageDashboardPage } from './pages/UsageDashboardPage';
import { RecruiterDashboardPage } from './pages/RecruiterDashboardPage';
import { SavedCandidatesPage } from './pages/SavedCandidatesPage';
import { RecruiterEvaluationPage } from './pages/RecruiterEvaluationPage';
import { RecruiterContactsPage } from './pages/RecruiterContactsPage';
import { TrainingDashboardPage } from './pages/TrainingDashboardPage';
import { TrainingModuleEditorPage } from './pages/TrainingModuleEditorPage';
import { AssignTrainingModulePage } from './pages/AssignTrainingModulePage';
import { ChallengesPage } from './pages/ChallengesPage';
import { ChallengeDetailPage } from './pages/ChallengeDetailPage';
import { MyAssignmentsPage } from './pages/MyAssignmentsPage';
import ContentSetsPage from './pages/ContentSetsPage';
import { CoachDashboardPage } from './pages/CoachDashboardPage';
import { ClientCoachingPage } from './pages/ClientCoachingPage';
import { OrgAnalyticsOverviewPage } from './pages/OrgAnalyticsOverviewPage';
import { OrgTrainingAnalyticsPage } from './pages/OrgTrainingAnalyticsPage';
import { OrgModuleAnalyticsDetailPage } from './pages/OrgModuleAnalyticsDetailPage';
import { OrgCoachingAnalyticsPage } from './pages/OrgCoachingAnalyticsPage';
import { MySuccessTimelinePage } from './pages/MySuccessTimelinePage';
import { MyCapabilitiesPage } from './pages/MyCapabilitiesPage';
import { MyReferralPage } from './pages/MyReferralPage';
import { VerifyCredentialPage } from './pages/VerifyCredentialPage';
import { SharedItemsPage } from './pages/SharedItemsPage';

function App() {
  // NotificationProvider is mounted ONCE here — it owns the singleton SSE
  // connections (notification stream + central event stream). Internally it
  // gates on (token, workspaceId), so it is safe to mount above public routes.
  return (
    <NotificationProvider>
    <Routes>
      {/* ============================================ */}
      {/* PUBLIC ROUTES - No authentication required */}
      {/* ============================================ */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<LoginPage />} />

      {/* Public "Try It" demo page (no auth required) */}
      <Route path="/try" element={<TryPage />} />

      {/* Public Pricing page (no auth required) */}
      <Route path="/pricing" element={<PricingPage />} />

      {/* Public Profile routes (no auth required) */}
      <Route path="/u/:slug" element={<PublicPortfolioPage />} />
      <Route path="/p/:slug" element={<PublicPortfolioPage />} />
      {/* Where the server-rendered /u and /p pages hand visitors off: nginx sends
          /u/* and /p/* to the backend (link-preview tags), so the SPA lives here. */}
      <Route path="/view/u/:slug" element={<PublicPortfolioPage />} />
      <Route path="/view/p/:slug" element={<PublicPortfolioPage />} />

      {/* Public Credential Verification (no auth required) */}
      <Route path="/verify/:hash" element={<VerifyCredentialPage />} />

      {/* Scoped share link (no auth required). The token is in the URL fragment: /s#<token> */}
      <Route path="/s" element={<SharedItemsPage />} />

      {/* ============================================ */}
      {/* ONBOARDING - Auth required, but NOT onboarding-gated */}
      {/* (otherwise we'd have a redirect loop!) */}
      {/* ============================================ */}
      <Route
        path="/onboarding"
        element={
          <AuthGuard>
            <BootGate>
              <OnboardingWizardPage />
            </BootGate>
          </AuthGuard>
        }
      />

      {/* ============================================ */}
      {/* ADMIN ROUTES — capability-driven: the server's admin functions */}
      {/* (GET /capabilities → admin.functions; Central platform admin + */}
      {/* each route's capability). Each screen needs ITS function; the */}
      {/* server enforces every route independently. */}
      {/* Order matters: AuthGuard → BootGate → AdminGuard. The guard reads */}
      {/* the boot's capabilities, so it must sit INSIDE the BootGate that */}
      {/* performs the boot (it used to sit outside: a direct load hung). */}
      {/* ============================================ */}
      <Route
        path="/admin"
        element={
          <AuthGuard>
            <BootGate>
              <AdminGuard>
                <BeheardShellLayout />
              </AdminGuard>
            </BootGate>
          </AuthGuard>
        }
      >
        <Route index element={<AdminLandingPage />} />
        <Route element={<AdminGuard fn="platform_dashboard" />}>
          <Route path="platform" element={<AdminDashboardPage />} />
        </Route>
        <Route element={<AdminGuard fn="usage_overview" />}>
          <Route path="usage" element={<AdminUsageOverviewPage />} />
        </Route>
        {/* Scenario authoring + its "Preview assessment" tab (authoring/QA, never an attempt) */}
        <Route element={<AdminGuard fn="scenario_authoring" />}>
          <Route path="scenarios" element={<ScenarioAuthoringListPage />} />
          <Route path="scenarios/:scenarioId/progression" element={<ScenarioAuthoringPage />} />
          <Route path="scenarios/:scenarioId/preview" element={<ScenarioAssessmentPreviewPage />} />
        </Route>
      </Route>

      {/* ============================================ */}
      {/* PROTECTED ROUTES - Auth + Onboarding required */}
      {/* ============================================ */}
      <Route
        element={
          <AuthGuard>
            <BootGate>
              <OnboardingGuard>
                <BeheardShellLayout />
              </OnboardingGuard>
            </BootGate>
          </AuthGuard>
        }
      >
        {/* Dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<HomeDashboardPage />} />

        {/* Practice */}
        <Route path="/practice" element={<PracticePage />} />
        <Route path="/practice/sessions/:sessionId/review" element={<PracticeReviewPage as="owner" />} />

        {/* NPC Progress */}
        <Route path="/npc/progress" element={<NpcProgressPage />} />

        {/* Story Prep routes */}
        <Route path="/petals" element={<PetalsManagerPage />} />
        <Route path="/blueprints" element={<BlueprintEditorPage />} />
        <Route path="/blueprints/:blueprintId" element={<BlueprintEditorPage />} />

        {/* AI Coaching routes */}
        <Route path="/coaching" element={<CoachingPage />} />
        {/* One conversation's result (the parameter used to be ignored) */}
        <Route path="/coaching/:sessionId" element={<CoachingSessionResultPage />} />

        {/* Interview Prep (P5): a setup form in front of the same coaching session */}
        <Route path="/interview-prep" element={<InterviewPrepPage />} />
        {/* Song learning (P5.5): optional; the page hides itself while the server has it off */}
        <Route path="/song-learning" element={<SongLearningPage />} />

        {/* Language Learning (P5): a setup form in front of the same coaching session, and per-language progress */}
        <Route path="/language-learning" element={<LanguageLearningPage />} />
        <Route path="/language-learning/progress" element={<LanguageProgressPage />} />

        {/* Human Coaching routes — coach dashboard requires coaching:write */}
        <Route element={<CapabilityGuard domain="coaching" action="write" />}>
          <Route path="/coach/dashboard" element={<CoachDashboardPage />} />
          <Route path="/coach/sessions/:sessionId/review" element={<PracticeReviewPage as="coach" />} />
        </Route>
        <Route path="/coaching/my-coach" element={<ClientCoachingPage />} />

        {/* Learn routes */}
        <Route path="/learn" element={<LearnPage />} />
        <Route path="/learn/:category/:slug" element={<LearnModulePage />} />

        {/* Marketplace routes */}
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/marketplace/experts" element={<ExpertsPage />} />
        <Route path="/marketplace/pitches" element={<PitchesPage />} />
        <Route path="/marketplace/hire-requests" element={<HireRequestsPage />} />

        {/* Leaderboard */}
        <Route path="/leaderboard" element={<LeaderboardPage />} />

        {/* Certifications */}
        <Route path="/certifications" element={<CertificationsPage />} />
        <Route path="/certifications/:domainKey" element={<CertificationsPage />} />

        {/* Skills & Assessment routes */}
        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/assessments/:objectiveId/run" element={<AssessmentRunPage />} />

        {/* Progress & Profile */}
        <Route path="/progress" element={<ProgressPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />

        {/* Settings v2 (D13): the universal Settings — one page for every tab,
            BeHeard's own included (Role & Access, Referrals, Usage, Public
            profile). Old URLs (/settings/billing, /settings/roles,
            /settings/referrals, …) keep working: the tab comes from the path. */}
        <Route path="/settings" element={<SettingsPageV2 />} />
        <Route path="/settings/:tab" element={<SettingsPageV2 />} />

        {/* Success Timeline */}
        <Route path="/my/timeline" element={<MySuccessTimelinePage />} />

        {/* Billing route (pricing is public, billing requires auth) — backward-compat alias */}
        <Route path="/billing" element={<BillingPage />} />

        {/* My Capabilities route */}
        <Route path="/my/capabilities" element={<MyCapabilitiesPage />} />

        {/* Usage & Limits Dashboard */}
        <Route path="/usage" element={<UsageDashboardPage />} />

        {/* Referral (Invite) */}
        <Route path="/referrals" element={<MyReferralPage />} />

        {/* Offload Responsibilities — wired from shell LeftNavigation OffloadButton */}
        <Route path="/offload-responsibilities" element={<OffloadResponsibilitiesPage />} />

        {/* Recruiter routes — require recruiter:read */}
        <Route element={<CapabilityGuard domain="recruiter" action="read" />}>
          <Route path="/recruiter" element={<RecruiterDashboardPage />} />
          <Route path="/recruiter/saved" element={<SavedCandidatesPage />} />
          <Route path="/recruiter/contacts" element={<RecruiterContactsPage />} />
          <Route path="/recruiter/evaluations" element={<RecruiterEvaluationPage />} />
          <Route path="/recruiter/evaluations/:sessionId" element={<RecruiterEvaluationPage />} />
        </Route>

        {/* Training routes — require training:read */}
        <Route element={<CapabilityGuard domain="training" action="read" />}>
          <Route path="/my-training" element={<MyAssignmentsPage />} />
          <Route path="/training" element={<TrainingDashboardPage />} />
          <Route path="/training/:orgId" element={<TrainingDashboardPage />} />
          {/* Team challenges (P3.5): training:read to view/join; creating needs training:write */}
          <Route path="/challenges" element={<ChallengesPage />} />
          <Route path="/challenges/:id" element={<ChallengeDetailPage />} />
        </Route>

        {/* Training administration — Central training:write (the server's check): create / edit / publish / archive / assign */}
        <Route element={<CapabilityGuard domain="training" action="write" />}>
          <Route path="/training/modules/new" element={<TrainingModuleEditorPage />} />
          <Route path="/training/modules/:moduleId" element={<TrainingModuleEditorPage />} />
          <Route path="/training/modules/:moduleId/assign" element={<AssignTrainingModulePage />} />
        </Route>

        {/* Content Library route */}
        <Route path="/content" element={<ContentSetsPage />} />

        {/* Organization Analytics routes — require org:read */}
        <Route element={<CapabilityGuard domain="org" action="read" />}>
          <Route path="/org/:orgSlug/analytics" element={<OrgAnalyticsOverviewPage />} />
          <Route path="/org/:orgSlug/analytics/training" element={<OrgTrainingAnalyticsPage />} />
          <Route path="/org/:orgSlug/analytics/training/modules/:moduleId" element={<OrgModuleAnalyticsDetailPage />} />
          <Route path="/org/:orgSlug/analytics/coaching" element={<OrgCoachingAnalyticsPage />} />
        </Route>
      </Route>

      {/* ============================================ */}
      {/* FALLBACK - Catch all unmatched routes */}
      {/* ============================================ */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </NotificationProvider>
  );
}

export default App;
